import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, ArrowRight, Briefcase, Search, 
  UserCheck, FileText, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '@/components/SEO';

const API = '/api';

const roleTypes = [
  'Site Managers & Supervisors',
  'Project Engineers & Civil Engineers',
  'Estimators & Quantity Surveyors',
  'Electricians, Plumbers & Tradespeople',
  'Safety & HSE Professionals',
  'Maintenance & Industrial Staff',
  'Project Coordinators & Admin',
  'Warehouse & Logistics Roles',
];

const benefits = [
  { icon: Search, title: 'Access Hidden Opportunities', desc: 'Many roles are filled before being advertised. Get early access through our network.' },
  { icon: UserCheck, title: 'Career Guidance', desc: 'Honest advice on your options and market positioning.' },
  { icon: Briefcase, title: 'Quality Employers', desc: 'We only work with reputable companies who value their people.' },
  { icon: FileText, title: 'Simple Process', desc: 'No lengthy applications. One registration gives you access to multiple opportunities.' },
];

const expectations = [
  'We\'ll contact you within 48 hours of registration',
  'Honest conversation about your experience and goals',
  'Only contacted for genuinely suitable opportunities',
  'Your details kept confidential and never shared without consent',
  'Support throughout interview and onboarding process',
  'Follow-up after placement to ensure satisfaction',
];

export default function Candidates() {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    desired_role: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/candidate`, formData);
      toast.success(response.data.message);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        desired_role: '',
        location: '',
        message: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="candidates-page">
      <SEO page="candidates" />
      {/* Hero */}
      <section className="pt-32 pb-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-4">For Candidates</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase mb-6">
              Your Next Career Move Starts Here
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mb-8">
              Looking for your next role in construction, engineering or trades? Register with Vulcan Recruitment and access opportunities across Australia.
            </p>
            <a href="#register">
              <Button className="bg-[#F5A623] hover:bg-[#D98E15] text-slate-900 font-bold py-6 px-8 uppercase tracking-wide text-base">
                Register Your Interest <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Role Types */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Roles We Support</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-6">
                Opportunities Across Industries
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Whether you're a qualified tradesperson, experienced engineer, or skilled site professional, we work with employers looking for talent just like you.
              </p>
              <Link to="/jobs">
                <Button className="bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold uppercase tracking-wide">
                  View Current Jobs <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {roleTypes.map((role, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="flex items-center gap-3 bg-slate-50 p-4 border border-slate-200"
                >
                  <CheckCircle className="w-5 h-5 text-[#1A8B9D] flex-shrink-0" />
                  <span className="text-slate-700 text-sm font-medium">{role}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Why Register</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase">
              Benefits Of Working With Us
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 border border-slate-200 text-center"
              >
                <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-[#1A8B9D]" />
                </div>
                <h3 className="font-heading text-lg font-bold text-slate-900 uppercase mb-2">{benefit.title}</h3>
                <p className="text-slate-600 text-sm">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What To Expect */}
      <section className="py-20 md:py-28 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">What To Expect</p>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-6">
                A Straightforward Process
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed">
                No endless forms or automated responses. When you register with us, you'll hear from a real person who understands your industry and can have a genuine conversation about your career goals.
              </p>
            </div>
            <div className="space-y-4">
              {expectations.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-start gap-4 bg-slate-800/50 p-4"
                >
                  <CheckCircle className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" />
                  <p className="text-slate-300">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register" className="py-20 md:py-28 bg-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="text-[#F5A623] font-semibold uppercase tracking-wider mb-3">Get Started</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-4">
              Register Your Interest
            </h2>
            <p className="text-slate-600">
              Complete the form below and we'll be in touch within 48 hours.
            </p>
          </div>
          
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="bg-slate-50 p-8 md:p-12 border border-slate-200"
            data-testid="candidate-form"
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Full Name *</label>
                <Input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D] bg-white"
                  placeholder="Your full name"
                  data-testid="candidate-form-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D] bg-white"
                  placeholder="your@email.com"
                  data-testid="candidate-form-email"
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Phone *</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D] bg-white"
                  placeholder="+61 xxx xxx xxx"
                  data-testid="candidate-form-phone"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Desired Role *</label>
                <Input
                  type="text"
                  name="desired_role"
                  value={formData.desired_role}
                  onChange={handleChange}
                  required
                  className="h-12 border-slate-300 focus:border-[#1A8B9D] bg-white"
                  placeholder="e.g., Electrician, Project Engineer"
                  data-testid="candidate-form-role"
                />
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">Preferred Location *</label>
              <Input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                required
                className="h-12 border-slate-300 focus:border-[#1A8B9D] bg-white"
                placeholder="e.g., Melbourne, VIC or Open to relocation"
                data-testid="candidate-form-location"
              />
            </div>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-2">About You / Additional Info</label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="min-h-[120px] border-slate-300 focus:border-[#1A8B9D] bg-white"
                placeholder="Tell us about your experience, qualifications and what you're looking for..."
                data-testid="candidate-form-message"
              />
            </div>
            
            {/* CV Instructions */}
            <div className="bg-teal-50 p-6 mb-8 border border-teal-100">
              <div className="flex items-start gap-4">
                <Mail className="w-6 h-6 text-[#1A8B9D] flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-slate-900 mb-1">Send Your CV</p>
                  <p className="text-slate-600 text-sm">
                    Please email your CV to <a href="mailto:info@vulcanrecruitment.com" className="text-[#1A8B9D] font-medium hover:underline">info@vulcanrecruitment.com</a> with your name and desired role in the subject line.
                  </p>
                </div>
              </div>
            </div>
            
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#1A8B9D] hover:bg-[#115E6B] text-white font-bold py-4 uppercase tracking-wide text-base"
              data-testid="candidate-form-submit"
            >
              {isSubmitting ? 'Submitting...' : 'Register Interest'}
            </Button>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
